import { useMemo } from 'react';
import { useLocation, useParams } from 'react-router-dom';

/**
 * Custom hook that parses URL query parameters into a key-value object
 *
 * @returns {Record<string, string>} An object containing query parameters where
 *                                  keys are parameter names and values are their corresponding values
 *
 * @example
 * // For URL: https://example.com?name=John&age=25
 * const queryParams = useGetQueryParams();
 * // Returns: { name: "John", age: "25" }
 */
const useGetQueryParams = (): Record<string, string> => {
  const location = useLocation();

  const queryParams = useMemo(() => {
    const paramMap: Record<string, string> = {};
    const queryParamsArr = [...new URLSearchParams(location.search)];

    queryParamsArr.forEach(([key, value]) => {
      paramMap[key] = decodeURIComponent(value);
    });

    return paramMap;
  }, [location]);

  return queryParams;
};

const useGetRouteParams = (): Record<string, string> => {
  const paramObj = useParams();

  const routeParams = useMemo(() => paramObj, [paramObj]);

  return routeParams;
};

export { useGetQueryParams, useGetRouteParams };
